import { useState } from 'preact/hooks';
import { Icon } from './Icon';
import { formatBytes } from '../lib/format';
import type { ReleaseState } from '../lib/releases';

export const INSTALL_NOTE = {
  mac: 'Unsigned preview: the first time, right-click the app and choose Open.',
  windows: 'Unsigned preview: if SmartScreen appears, choose More info › Run anyway.',
};

/** A desktop download CTA that is only live when a real build is published (GET /v1/releases/latest). */
export function ReleaseButton({ state, platform, size = 'lg', label }: { state: ReleaseState; platform: 'mac' | 'windows'; size?: 'lg' | 'md'; label?: string }) {
  const name = platform === 'mac' ? 'Mac' : 'Windows';
  const asset = state.status === 'ready' ? state.release.assets[platform] : undefined;
  const cls = `btn primary${size === 'lg' ? ' lg' : ''}`;
  const icon = <Icon name={platform === 'mac' ? 'apple' : 'windows'} size={20} />;
  if (asset) {
    return <a class={cls} href={asset.url} download={asset.name} data-testid={`dl-${platform}`}>{icon}{label ?? `Download for ${name}`}</a>;
  }
  const reason = state.status === 'loading' ? 'Checking…' : 'Coming soon';
  return (
    <span class="stack" style="gap:4px;align-items:flex-start">
      <button class={cls} type="button" disabled aria-describedby={`dl-${platform}-why`} data-testid={`dl-${platform}`}>{icon}{label ?? `Download for ${name}`}</button>
      <span id={`dl-${platform}-why`} class="caption muted" data-testid={`dl-${platform}-status`}>{reason}</span>
    </span>
  );
}

function Checksum({ sha, platform }: { sha: string; platform: string }) {
  const [copied, setCopied] = useState<boolean | null>(null);
  const copy = async () => {
    try { await navigator.clipboard.writeText(sha); setCopied(true); } catch { setCopied(false); }
    setTimeout(() => setCopied(null), 2500);
  };
  return (
    <span class="checksum caption muted">
      SHA-256 <code title={sha} data-testid={`sha-${platform}`} data-sha={sha}>{sha.slice(0, 8)}…{sha.slice(-6)}</code>
      <button type="button" class="linkish" onClick={copy} aria-label={`Copy full SHA-256 checksum for the ${platform === 'mac' ? 'macOS' : 'Windows'} download`}>{copied === true ? 'Copied' : copied === false ? 'Copy failed' : 'Copy'}</button>
    </span>
  );
}

export function ReleaseMeta({ state, platform }: { state: ReleaseState; platform: 'mac' | 'windows' }) {
  if (state.status !== 'ready') return null;
  const a = state.release.assets[platform];
  if (!a) return null;
  return (
    <div class="release-meta" data-testid={`release-${platform}`}>
      <span>Version {state.release.tag.replace(/^v/, '')} · {formatBytes(a.bytes)} · <strong>Unsigned preview</strong></span>
      <span class="caption muted">{INSTALL_NOTE[platform]}</span>
      {a.sha256 && <Checksum sha={a.sha256} platform={platform} />}
    </div>
  );
}
